import { faUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { shallowEqual } from 'react-redux';
import BeatLoader from 'react-spinners/BeatLoader';
import type { Booth } from '../../interfaces/interfaces';
import { fetchAndSetAdminBooth } from '../../reduxToolkit/slices/adminSlice';
import { setPDFModal } from '../../reduxToolkit/slices/commonSlice';
import { RootState } from '../../reduxToolkit/store';
import { fileUpload, updateBooth } from '../../services/services';
import VyButton from '../common/VyButton';
import VyModal from '../common/VyModal';
import VyTextField from '../common/VyTextField';

import toast from 'react-hot-toast';
import { VYSTEMBLUE } from '../../static/constants';
import { useDispatch, useSelector } from '../../reduxToolkit/typedRedux';

const UploadPDF = ({ onRefresh, booth: boothProp, inline = true }: { booth?: Booth; onRefresh?: () => void; inline?: boolean }) => {
  const { t } = useTranslation();
  const [fileUploaded, setFileUploaded] = useState<string | null>(null);
  const { pdfModal } = useSelector((state: RootState) => ({ pdfModal: state.common.PDFModal }), shallowEqual);
  const booth: Booth | null = boothProp ?? useSelector((state: RootState) => state.admin.userbooth);

  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const { control, handleSubmit } = useForm();
  const dispatch = useDispatch();

  async function uploadFile({ target }: React.ChangeEvent<HTMLInputElement>) {
    if (!target?.files) return;
    setUploading(true);
    const data = await fileUpload(target.files[0]);
    setUploading(false);
    setFileUploaded(data.data.fileId);
  }

  async function submit({ pdfName }: { pdfName?: string }) {
    if (!booth || !pdfName || !fileUploaded) {
      console.log('returning', booth, pdfName, fileUpload);
      return;
    }
    const updateObj = {
      pdfUploads: [...(booth.pdfUploads ? booth.pdfUploads : []), { file: fileUploaded, name: pdfName }],
      boothid: booth._id,
    };
    setLoading(true);
    try {
      await updateBooth(updateObj);
      setModal(false);
      toast.success('Erfolgreich hochgeladen!');
      setFileUploaded(null);
      !inline && dispatch(fetchAndSetAdminBooth({ boothid: booth._id }));
      onRefresh?.();
    } catch (error) {
      console.log('Error while updating the booth ', error);
    }
    setLoading(false);
  }

  function setModal(open: boolean) {
    setFileUploaded(null);
    dispatch(setPDFModal(open));
  }

  return (
    <>
      <VyModal open={pdfModal} onClose={() => setModal(false)} hasDefaultPadding={false}>
        {() => (
          <div className="grid px-8 py-6 bg-white rounded place-items-center ">
            <form onSubmit={handleSubmit(submit)} name="pdf-form" className="flex flex-col justify-between [&>*]:my-2">
              <div className="grid place-items-center">
                {!uploading && !fileUploaded && (
                  <label className={`p-3 text-white flex items-center cursor-pointer bg-vystemblue rounded-md`}>
                    <FontAwesomeIcon style={{ height: '20px', width: '20px' }} icon={faUpload} />
                    <span className="ml-4">{true ? t('booth:upload-new-pdf') : t('booth:upload-pdf')}</span>
                    <input onChange={(e) => uploadFile(e)} accept="application/pdf" style={{ display: 'none' }} name="pdf" type="file" />
                  </label>
                )}
                <BeatLoader size={20} color="var(--vystem-blue)" loading={uploading && fileUploaded === null} />
                {fileUploaded && !uploading && <p>✔ {t('common:file-uploaded')}</p>}
              </div>
              <Controller control={control} name="pdfName" defaultValue="" render={({ field }) => <VyTextField {...field} label="Name" type="string" />} />
              <VyButton
                isSaveButton
                className="w-full mt-5"
                type="submit"
                name="pdf-submit"
                showLoadingIcon={loading}
                disabled={uploading || loading || !fileUploaded}>
                {t('common:save')}
              </VyButton>
            </form>
          </div>
        )}
      </VyModal>
    </>
  );
};

export default UploadPDF;
